/**
 * Commercial: billing and receivables, commitments and costs, variations, and
 * the project budget.
 */

import { ifMatch, request } from './client.js';

// ------------------------------------------------------------------- billing

export type InvoiceStatus = 'draft' | 'issued' | 'cancelled';
export type InvoiceStanding = 'draft' | 'cancelled' | 'paid' | 'part_paid' | 'due' | 'overdue';

export interface Receipt {
  readonly id: string;
  readonly reference: string | null;
  readonly received_on: string;
  readonly amount: string;
  readonly created_by_name: string | null;
}

export interface Invoice {
  readonly id: string;
  readonly reference: string;
  readonly description: string | null;
  readonly invoice_date: string;
  readonly due_date: string;
  readonly amount: string;
  readonly tax_amount: string;
  readonly gross: string;
  readonly status: InvoiceStatus;
  readonly cancelled_reason: string | null;
  readonly received: string;
  readonly outstanding: string;
  /** Worked out from the receipts and today's date, never stored. */
  readonly standing: InvoiceStanding;
  readonly days_overdue: number | null;
  readonly created_by_name: string | null;
  readonly receipts: readonly Receipt[];
}

export interface Receivables {
  readonly outstanding: string;
  readonly outstanding_count: number;
  readonly overdue: string;
  readonly overdue_count: number;
  readonly worst_days_overdue: number;
  readonly invoiced: string;
  readonly received: string;
}

export interface BillingView {
  readonly invoices: readonly Invoice[];
  readonly receivables: Receivables;
}

export function getBilling(projectId: string): Promise<BillingView> {
  return request<BillingView>(`/v1/projects/${encodeURIComponent(projectId)}/billing`);
}

export function createInvoice(
  projectId: string,
  input: {
    reference: string;
    description?: string;
    invoice_date: string;
    due_date: string;
    amount: string;
    tax_amount?: string;
    status?: 'draft' | 'issued';
  },
): Promise<BillingView> {
  return request<BillingView>(`/v1/projects/${encodeURIComponent(projectId)}/invoices`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function recordReceipt(
  projectId: string,
  invoiceId: string,
  input: { amount: string; received_on: string; reference?: string },
): Promise<BillingView> {
  return request<BillingView>(
    `/v1/projects/${encodeURIComponent(projectId)}/invoices/${encodeURIComponent(invoiceId)}/receipts`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function moveInvoice(
  projectId: string,
  invoiceId: string,
  input: { status: 'issued' | 'cancelled'; reason?: string },
): Promise<BillingView> {
  return request<BillingView>(
    `/v1/projects/${encodeURIComponent(projectId)}/invoices/${encodeURIComponent(invoiceId)}/status`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

// ------------------------------------------------------- commitments and costs

export const COMMITMENT_SOURCES = ['purchase_order', 'subcontract', 'other'] as const;

export interface Commitment {
  readonly id: string;
  readonly reference: string;
  readonly description: string | null;
  readonly source: string;
  readonly amount: string;
  readonly committed_on: string;
  readonly budget_line_id: string | null;
  readonly budget_category: string | null;
  readonly created_by_name: string | null;
}

export interface CostEntry {
  readonly id: string;
  readonly description: string;
  readonly reference: string | null;
  readonly source: string;
  readonly amount: string;
  readonly cost_date: string;
  readonly budget_line_id: string | null;
  readonly budget_category: string | null;
  readonly created_by_name: string | null;
}

export interface CostReportLine {
  readonly budget_line_id: string | null;
  readonly category: string;
  readonly allowed: string;
  readonly committed: string;
  readonly spent: string;
  readonly forecast: string;
  readonly variance: string;
  /** Which number produced the forecast, so a QS can argue with it. */
  readonly forecast_from: 'budget' | 'committed_and_spent';
  readonly over: boolean;
}

export interface CostReport {
  readonly lines: readonly CostReportLine[];
  readonly total_allowed: string;
  readonly total_committed: string;
  readonly total_spent: string;
  readonly total_forecast: string;
  readonly total_variance: string;
  readonly any_line_over: boolean;
}

export interface CostView {
  readonly report: CostReport;
  readonly commitments: readonly Commitment[];
  readonly costs: readonly CostEntry[];
}

export function getCosts(projectId: string): Promise<CostView> {
  return request<CostView>(`/v1/projects/${encodeURIComponent(projectId)}/costs`);
}

export function createCommitment(
  projectId: string,
  input: {
    reference: string;
    description?: string;
    source?: string;
    amount: string;
    committed_on: string;
    budget_line_id?: string | null;
  },
): Promise<CostView> {
  return request<CostView>(`/v1/projects/${encodeURIComponent(projectId)}/commitments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function createCostEntry(
  projectId: string,
  input: {
    description: string;
    reference?: string;
    amount: string;
    cost_date: string;
    budget_line_id?: string | null;
  },
): Promise<CostView> {
  return request<CostView>(`/v1/projects/${encodeURIComponent(projectId)}/cost-entries`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ImportReport {
  /** True only when rows were actually written. */
  readonly imported: boolean;
  readonly count: number;
  readonly problems: readonly { readonly line: number; readonly message: string }[];
  readonly duplicates: readonly { readonly line: number; readonly why: string }[];
  readonly unknown_categories: readonly string[];
  readonly view: CostView | null;
}

/**
 * A CSV of costs, as text.
 *
 * Not a file upload: nothing keeps this file, nothing serves it back, and it
 * exists only long enough to become rows. Putting a copy of the company's
 * ledger through the documents module and onto disk would be storing it for no
 * reason.
 */
export function importCostEntries(
  projectId: string,
  csv: string,
  options: { allowDuplicates?: boolean } = {},
): Promise<ImportReport> {
  return request<ImportReport>(
    `/v1/projects/${encodeURIComponent(projectId)}/cost-entries/import`,
    {
      method: 'POST',
      body: JSON.stringify({ csv, allow_duplicates: options.allowDuplicates === true }),
    },
  );
}

export function deleteCommitment(projectId: string, id: string): Promise<CostView> {
  return request<CostView>(
    `/v1/projects/${encodeURIComponent(projectId)}/commitments/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export function deleteCostEntry(projectId: string, id: string): Promise<CostView> {
  return request<CostView>(
    `/v1/projects/${encodeURIComponent(projectId)}/cost-entries/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

// ------------------------------------------------------------------- variations

export type VariationStatus =
  | 'potential_change'
  | 'under_review'
  | 'quotation_submitted'
  | 'approved'
  | 'rejected'
  | 'instructed'
  | 'executed'
  | 'billed'
  | 'collected'
  | 'withdrawn';

export const VARIATION_CAUSES = [
  'client_request',
  'design_development',
  'site_condition',
  'authority_requirement',
  'contractor_error',
  'other',
] as const;

export interface Variation {
  readonly id: string;
  /**
   * The concurrency token, opaque. Hand it back to `updateVariation` so a save
   * on top of somebody else's is refused rather than silently overwriting it.
   */
  readonly record_version: string;
  readonly number: string;
  readonly title: string;
  readonly description: string | null;
  readonly cause: string;
  readonly status: VariationStatus;
  readonly time_impact_days: number | null;
  readonly raised_by_name: string | null;
  readonly created_at: string;
  readonly instructed_without_approval: boolean;
  readonly instructed_at: string | null;
  readonly exposed_days: number | null;
  readonly allowed_next: readonly VariationStatus[];
  /** Absent — not null — without financial access on this project (ADR-005). */
  readonly cost_impact?: string | null;
  readonly value_impact?: string | null;
  readonly quoted_amount?: string | null;
  /** Recorded by a QS. Never calculated. */
  readonly margin_impact?: string | null;
  /** value − cost, offered only when no margin was recorded. */
  readonly margin_from_figures?: string | null;
}

export interface Exposure {
  /** The COST of work instructed and never approved — the money at risk. */
  readonly unapproved_cost: string;
  /** What that same work would have been billed at. Not the loss. */
  readonly unapproved_value: string;
  readonly unapproved_count: number;
  /** How many of those have no cost recorded, so the figure is incomplete. */
  readonly unapproved_unpriced_count: number;
  readonly approved_value: string;
  readonly approved_count: number;
  readonly unapproved_pct_of_contract: number | null;
  readonly contract_value: string | null;
}

export interface VariationView {
  readonly variations: readonly Variation[];
  /** How many the project holds, against how many are loaded here. */
  readonly total: number;
  readonly truncated: boolean;
  /**
   * Absent without financial access — it is money end to end.
   *
   * Computed over EVERY variation, never over the loaded page. A register
   * showing fewer rows is obvious; a money figure that is quietly too small is
   * not, and it is the one a director takes to a client meeting. See
   * `listVariations` on the server.
   */
  readonly exposure?: Exposure;
}

export function listVariations(projectId: string, limit?: number): Promise<VariationView> {
  const query = limit === undefined ? '' : `?limit=${limit}`;
  return request<VariationView>(
    `/v1/projects/${encodeURIComponent(projectId)}/variations${query}`,
  );
}

export interface VariationInput {
  title: string;
  /** Null on the wire is coerced to '' by the server's schema layer. */
  description?: string | null;
  cause?: string;
  cost_impact?: string | null;
  value_impact?: string | null;
  margin_impact?: string | null;
  time_impact_days?: number | null;
}

export function createVariation(projectId: string, input: VariationInput): Promise<VariationView> {
  return request<VariationView>(`/v1/projects/${encodeURIComponent(projectId)}/variations`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateVariation(
  projectId: string,
  variationId: string,
  input: Partial<VariationInput>,
  /** The `record_version` this edit was started from. See `ifMatch`. */
  expectedVersion?: string,
): Promise<VariationView> {
  return request<VariationView>(
    `/v1/projects/${encodeURIComponent(projectId)}/variations/${encodeURIComponent(variationId)}`,
    { method: 'PATCH', body: JSON.stringify(input), ...ifMatch(expectedVersion) },
  );
}

export function moveVariation(
  projectId: string,
  variationId: string,
  input: { status: VariationStatus; reason?: string },
): Promise<VariationView> {
  return request<VariationView>(
    `/v1/projects/${encodeURIComponent(projectId)}/variations/${encodeURIComponent(variationId)}/status`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

// --------------------------------------------------------------------- budgets

export interface BudgetLine {
  readonly id: string;
  readonly category: string;
  readonly description: string | null;
  /** Decimal STRINGS. A float in a money path is a defect. */
  readonly baseline_amount: string | null;
  readonly revised_amount: string | null;
  readonly currency: string | null;
  /** revised − baseline. Null when either is unset. */
  readonly movement: string | null;
  /**
   * The concurrency token, opaque. Hand it back to `updateBudgetLine` so a save
   * on top of somebody else's is refused rather than silently overwriting it.
   */
  readonly record_version: string;
}

export interface BudgetView {
  readonly lines: readonly BudgetLine[];
  readonly baseline_total: string;
  readonly revised_total: string;
  readonly currency: string;
}

const budgetPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/budget`;

/**
 * The whole resource is money, so a caller without financial access is refused
 * the call itself rather than handed a stripped row.
 */
export function listBudget(projectId: string): Promise<BudgetView> {
  return request<BudgetView>(budgetPath(projectId));
}

export function createBudgetLine(
  projectId: string,
  input: {
    category: string;
    description?: string | null;
    baseline_amount?: string | null;
    revised_amount?: string | null;
  },
): Promise<BudgetView> {
  return request<BudgetView>(budgetPath(projectId), {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateBudgetLine(
  projectId: string,
  lineId: string,
  input: {
    category?: string;
    description?: string | null;
    baseline_amount?: string | null;
    revised_amount?: string | null;
  },
  /** The `record_version` this edit was started from. See `ifMatch`. */
  expectedVersion?: string,
): Promise<BudgetView> {
  return request<BudgetView>(`${budgetPath(projectId)}/${encodeURIComponent(lineId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
    ...ifMatch(expectedVersion),
  });
}

export function deleteBudgetLine(projectId: string, lineId: string): Promise<BudgetView> {
  return request<BudgetView>(`${budgetPath(projectId)}/${encodeURIComponent(lineId)}`, {
    method: 'DELETE',
  });
}
